/**
 * 30-day rolling trend chart for the Super Admin dashboard.
 * Uses recharts (already in the bundle) and plots multiple series on one axis.
 */
import { useEffect, useState } from "react";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend } from "recharts";
import api from "@/lib/api";
import { Loader2, TrendingUp } from "lucide-react";

const SERIES = [
  { key: "new_mrr_pence", label: "New MRR (£)", color: "#D4AF37", transform: (v) => v / 100 },
  { key: "active_subscribers", label: "Active subs", color: "#34D399" },
  { key: "trial_conversions", label: "Trial → paid", color: "#60A5FA" },
  { key: "failed_payments", label: "Failed payments", color: "#F87171" },
  { key: "churned", label: "Churned", color: "#A78BFA" },
];

const fmtAxis = (iso) => {
  try { return new Date(iso).toLocaleDateString(undefined, { day: "numeric", month: "short" }); }
  catch { return iso; }
};

export default function RevenueTrendChart() {
  const [data, setData] = useState(null);
  const [days, setDays] = useState(30);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get(`/admin/church-builder/trend?days=${days}`)
      .then((r) => setData(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [days]);

  const buckets = data?.buckets || [];
  const transformed = buckets.map((b) => ({
    ...b,
    new_mrr_pence: (b.new_mrr_pence || 0) / 100,
  }));

  return (
    <div data-testid="cb-admin-trend" className="luxury-card rounded-xl p-5 mt-6 border border-white/10">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <TrendingUp size={16} className="text-[#D4AF37]" />
          <div>
            <div className="font-serif-display text-xl">Revenue & Activity Trend</div>
            <div className="text-xs text-neutral-400 mt-0.5">Daily rollups across active subscribers, new MRR, conversions and churn.</div>
          </div>
        </div>
        <div className="inline-flex border border-white/10 rounded-full p-1">
          {[7, 30, 90].map((d) => (
            <button key={d} data-testid={`cb-trend-${d}d`} onClick={() => setDays(d)}
              className={`px-3 py-1 text-[10px] tracking-widest uppercase rounded-full ${days === d ? "bg-[#D4AF37] text-black" : "text-neutral-400"}`}>
              {d}d
            </button>
          ))}
        </div>
      </div>

      {data?.totals && (
        <div className="mt-5 grid grid-cols-3 gap-3 text-center">
          <PlanCount label="Starter" value={data.totals.starter_active} tone="#94A3B8" />
          <PlanCount label="Standard" value={data.totals.standard_active} tone="#D4AF37" />
          <PlanCount label="Premium" value={data.totals.premium_active} tone="#F472B6" />
        </div>
      )}

      <div className="mt-6 h-72 w-full" style={{ minHeight: 288 }}>
        {loading ? (
          <div className="h-full flex items-center justify-center"><Loader2 className="animate-spin text-[#D4AF37]" size={18} /></div>
        ) : transformed.length === 0 ? (
          <div className="h-full flex items-center justify-center text-xs text-neutral-500">No data yet — once churches start subscribing, the chart populates here.</div>
        ) : (
          <ResponsiveContainer width="100%" height={288}>
            <LineChart data={transformed} margin={{ top: 8, right: 20, bottom: 0, left: 0 }}>
              <CartesianGrid stroke="rgba(255,255,255,0.05)" />
              <XAxis dataKey="date" stroke="#666" tick={{ fontSize: 10 }} tickFormatter={fmtAxis} />
              <YAxis stroke="#666" tick={{ fontSize: 10 }} />
              <Tooltip
                contentStyle={{ background: "#0A0A0A", border: "1px solid rgba(255,255,255,0.12)", borderRadius: 6, fontSize: 12 }}
                labelStyle={{ color: "#D4AF37" }}
                labelFormatter={fmtAxis}
              />
              <Legend wrapperStyle={{ fontSize: 10, paddingTop: 8 }} />
              {SERIES.map((s) => (
                <Line key={s.key} type="monotone" dataKey={s.key} name={s.label} stroke={s.color} strokeWidth={2} dot={false} />
              ))}
            </LineChart>
          </ResponsiveContainer>
        )}
      </div>
    </div>
  );
}

function PlanCount({ label, value, tone }) {
  return (
    <div data-testid={`cb-trend-plan-${label.toLowerCase()}`} className="rounded-lg border border-white/5 bg-black/30 p-3">
      <div className="text-[10px] tracking-widest uppercase" style={{ color: tone }}>{label} Active</div>
      <div className="font-serif-display text-2xl text-white mt-1">{value}</div>
    </div>
  );
}
